/**
 * Mantenimiento → Dashboard (`/mantenimiento`).
 *
 * Puerto de `Maintenance::index` + `views/maintenance/dashboard.php`: los cuatro
 * indicadores del panel anterior (activos, sub-activos críticos, tareas
 * pendientes y total de tareas), la lista de sub-activos que ya pasaron su
 * umbral, las tareas pendientes de aprobación y las últimas realizadas.
 *
 * Se añaden dos tarjetas (sub-activos en aviso y tareas por aprobar), el resumen
 * por categoría y los accesos directos a las demás pantallas del módulo.
 */

import {
  ArrowLeft,
  Boxes,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Gauge,
  Layers,
  ShieldAlert,
  TriangleAlert,
  Wrench,
  WrenchIcon,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatNumber } from '@/lib/utils';

import { useDashboardMantenimiento } from '../resumen.api';
import { varianteEstado, type SubActivo } from '../sub-activos.types';
import type { CategoriaResumen, TareaResumen } from '../dashboard.types';

/** Color de la barra de consumo según el estado del sub-activo. */
const BARRA: Record<string, string> = {
  danger: 'bg-rose-500',
  warning: 'bg-amber-500',
  success: 'bg-emerald-500',
  neutral: 'bg-ink-300',
};

/** Tarjeta de indicador. */
function Indicador({
  titulo,
  valor,
  nota,
  icono: Icono,
  tono,
  to,
}: {
  titulo: string;
  valor: number;
  nota: string;
  icono: typeof Wrench;
  tono: 'brand' | 'danger' | 'warning' | 'success';
  to: string;
}) {
  const tonos = {
    brand: 'border-brand-100 bg-brand-50/60 text-brand-700',
    danger: 'border-rose-100 bg-rose-50/60 text-rose-700',
    warning: 'border-amber-100 bg-amber-50/60 text-amber-700',
    success: 'border-emerald-100 bg-emerald-50/60 text-emerald-700',
  };

  return (
    <Link
      to={to}
      className={cn(
        'flex flex-col gap-1 rounded-card border p-4 transition-shadow hover:shadow-card',
        tonos[tono],
      )}
    >
      <span className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider opacity-80">{titulo}</span>
        <Icono className="size-4" aria-hidden="true" />
      </span>
      <span className="text-3xl font-bold tabular-nums">{formatNumber(valor)}</span>
      <span className="text-[11px] opacity-70">{nota}</span>
    </Link>
  );
}

/** Fila del listado de sub-activos críticos. */
function FilaCritica({ subActivo }: { subActivo: SubActivo }) {
  return (
    <tr className="border-b border-ink-50 last:border-0">
      <td className="py-2.5">
        <span className="block text-sm font-semibold text-ink-900">{subActivo.nombre}</span>
        <span className="block text-[11px] text-ink-400">
          {subActivo.activo} · {subActivo.categoria}
        </span>
      </td>
      <td className="py-2.5">
        <div className="flex min-w-28 flex-col gap-1">
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-ink-100">
            <div
              className={cn('h-full rounded-full', BARRA[subActivo.estado_color] ?? 'bg-ink-300')}
              style={{ width: `${subActivo.progreso}%` }}
            />
          </div>
          <span className="text-[11px] text-ink-400">
            {formatNumber(subActivo.usado)} de {formatNumber(subActivo.umbral)}
          </span>
        </div>
      </td>
      <td className="py-2.5 text-right">
        <Badge variant={varianteEstado(subActivo.estado_color)}>
          {subActivo.estado_etiqueta}
        </Badge>
      </td>
    </tr>
  );
}

/** Fila de tarea resumida. */
function FilaTarea({ tarea }: { tarea: TareaResumen }) {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-ink-50 py-2.5 last:border-0">
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-ink-800">{tarea.tipo}</span>
        <span className="block text-[11px] text-ink-400">
          {tarea.sub_activo} · {tarea.activo}
          {tarea.ejecutado_por ? ` · ${tarea.ejecutado_por}` : ''}
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center justify-end gap-2">
        <Badge variant={tarea.prioridad === 'CRÍTICA' ? 'danger' : 'neutral'}>
          {tarea.prioridad}
        </Badge>
        {tarea.ejecucion ? (
          <Badge variant="outline">
            <CalendarClock className="size-3" aria-hidden="true" />
            {tarea.ejecucion}
          </Badge>
        ) : null}
        {tarea.aprobada ? (
          <Badge variant="success">
            <CheckCircle2 className="size-3" aria-hidden="true" />
          </Badge>
        ) : null}
      </span>
    </li>
  );
}

/** Fila del resumen por categoría. */
function FilaCategoria({ categoria }: { categoria: CategoriaResumen }) {
  return (
    <tr className="border-b border-ink-50 last:border-0">
      <td className="py-2.5">
        <span className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className="inline-block size-3 rounded-full border border-ink-200"
            style={{ backgroundColor: categoria.color }}
          />
          <span className="text-sm font-medium text-ink-800">{categoria.categoria}</span>
          {categoria.activa ? null : <Badge variant="neutral">inactiva</Badge>}
        </span>
      </td>
      <td className="py-2.5 text-center tabular-nums text-ink-700">{categoria.activos}</td>
      <td className="py-2.5 text-center tabular-nums text-ink-700">{categoria.sub_activos}</td>
      <td className="py-2.5 text-center">
        {categoria.criticos > 0 ? (
          <Badge variant="danger">{categoria.criticos}</Badge>
        ) : (
          <span className="text-ink-300">—</span>
        )}
      </td>
      <td className="py-2.5 text-center">
        {categoria.pendientes > 0 ? (
          <Badge variant="warning">{categoria.pendientes}</Badge>
        ) : (
          <span className="text-ink-300">—</span>
        )}
      </td>
    </tr>
  );
}

export function MantenimientoDashboardPage() {
  useDocumentTitle('Mantenimiento · Dashboard');

  const dashboard = useDashboardMantenimiento();
  const sinPermiso = dashboard.error instanceof ApiError && dashboard.error.status === 403;
  const datos = dashboard.data;

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/inicio/mantenimiento"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Mantenimiento
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <WrenchIcon className="size-6 text-brand-500" aria-hidden="true" />
          Panel de mantenimiento
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Qué activos hay vigilados, cuáles ya pasaron su umbral de galonaje y qué tareas están
          pendientes o a la espera de aprobación.
        </p>
      </div>

      {datos ? (
        <Badge variant={datos.kpis.criticos > 0 ? 'danger' : 'success'}>
          <Gauge className="size-3" aria-hidden="true" />
          {datos.kpis.criticos} en crítico · {datos.kpis.en_aviso} en aviso
        </Badge>
      ) : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>maintenance</strong>.
          </span>
        </Alert>
      </div>
    );
  }

  if (dashboard.isLoading) {
    return (
      <div className="space-y-6">
        {cabecera}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
        </div>
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (dashboard.error || !datos) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="error" title="No se pudo leer el panel">
          {dashboard.error instanceof Error ? dashboard.error.message : 'La API no respondió.'}
        </Alert>
      </div>
    );
  }

  const { kpis } = datos;

  return (
    <div className="space-y-6">
      {cabecera}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Indicador
          titulo="Activos"
          valor={kpis.activos}
          nota={`${kpis.activos_vigentes} vigentes`}
          icono={Layers}
          tono="brand"
          to="/mantenimiento/activos"
        />
        <Indicador
          titulo="Sub-activos críticos"
          valor={kpis.criticos}
          nota={`${kpis.en_aviso} próximos a vencer`}
          icono={TriangleAlert}
          tono="danger"
          to="/mantenimiento/sub-activos"
        />
        <Indicador
          titulo="Tareas pendientes"
          valor={kpis.pendientes}
          nota={`${kpis.en_proceso} en proceso`}
          icono={ClipboardList}
          tono="warning"
          to="/mantenimiento/tareas"
        />
        <Indicador
          titulo="Total de tareas"
          valor={kpis.tareas}
          nota={`${kpis.realizadas} realizadas`}
          icono={CheckCircle2}
          tono="success"
          to="/mantenimiento/reportes"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wider text-ink-400">
                Sub-activos
              </span>
              <span className="text-xl font-bold text-ink-900">
                {formatNumber(kpis.sub_activos)}
              </span>
            </span>
            <Boxes className="size-5 text-brand-500" aria-hidden="true" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wider text-ink-400">
                Tareas por aprobar
              </span>
              <span className="text-xl font-bold text-ink-900">
                {formatNumber(kpis.por_aprobar)}
              </span>
            </span>
            <ShieldAlert className="size-5 text-amber-500" aria-hidden="true" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center justify-between gap-3">
            <span>
              <span className="block text-xs font-semibold uppercase tracking-wider text-ink-400">
                Categorías
              </span>
              <span className="text-xl font-bold text-ink-900">
                {formatNumber(kpis.categorias)}
              </span>
            </span>
            <Wrench className="size-5 text-ink-400" aria-hidden="true" />
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <TriangleAlert className="size-4 text-rose-500" aria-hidden="true" />
              Sub-activos críticos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {datos.criticos.length === 0 ? (
              <Alert variant="success" title="Nada crítico">
                Ningún sub-activo pasó todavía su umbral de galonaje.
              </Alert>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                      <th className="py-2">Componente</th>
                      <th className="py-2">Consumo</th>
                      <th className="py-2 text-right">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.criticos.map((subActivo) => (
                      <FilaCritica key={subActivo.id} subActivo={subActivo} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <ShieldAlert className="size-4 text-amber-500" aria-hidden="true" />
              Tareas por aprobar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {datos.por_aprobar.length === 0 ? (
              <Alert variant="info">No hay tareas realizadas esperando aprobación.</Alert>
            ) : (
              <ul className="flex flex-col">
                {datos.por_aprobar.map((tarea) => (
                  <FilaTarea key={tarea.id} tarea={tarea} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <CheckCircle2 className="size-4 text-emerald-500" aria-hidden="true" />
              Últimas tareas realizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            {datos.realizadas.length === 0 ? (
              <Alert variant="info">Todavía no se ha realizado ninguna tarea.</Alert>
            ) : (
              <ul className="flex flex-col">
                {datos.realizadas.map((tarea) => (
                  <FilaTarea key={tarea.id} tarea={tarea} />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center gap-2">
              <Wrench className="size-4 text-brand-500" aria-hidden="true" />
              Resumen por categoría
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                    <th className="py-2">Categoría</th>
                    <th className="py-2 text-center">Activos</th>
                    <th className="py-2 text-center">Sub-activos</th>
                    <th className="py-2 text-center">Críticos</th>
                    <th className="py-2 text-center">Pendientes</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.por_categoria.map((categoria) => (
                    <FilaCategoria key={categoria.id} categoria={categoria} />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
